import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post("/auth/register", data),
  login: (data) => api.post("/auth/login", data),
  me: () => api.get("/auth/me"),
};

// Vehicles
export const vehiclesAPI = {
  list: () => api.get("/vehicles"),
  get: (id) => api.get(`/vehicles/${id}`),
  create: (data) => api.post("/vehicles", data),
  update: (id, data) => api.put(`/vehicles/${id}`, data),
  delete: (id) => api.delete(`/vehicles/${id}`),
  dashboard: (id) => api.get(`/vehicles/${id}/dashboard`),
  uploadPhoto: (id, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/vehicles/${id}/photo`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  comparisonData: (id) => api.get(`/vehicles/${id}/comparison-data`),
};

// Services
export const servicesAPI = {
  list: (vehicleId) => api.get(`/vehicles/${vehicleId}/services`),
  get: (id) => api.get(`/services/${id}`),
  create: (data) => api.post("/services", data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
  uploadAttachment: (id, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/services/${id}/attachments`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// Mods
export const modsAPI = {
  list: (vehicleId) => api.get(`/vehicles/${vehicleId}/mods`),
  get: (id) => api.get(`/mods/${id}`),
  create: (data) => api.post("/mods", data),
  update: (id, data) => api.put(`/mods/${id}`, data),
  delete: (id) => api.delete(`/mods/${id}`),
  uploadPhoto: (id, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/mods/${id}/photos`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// Enums
export const enumsAPI = {
  serviceCategories: () => api.get("/enums/service-categories"),
  modCategories: () => api.get("/enums/mod-categories"),
  modCategoriesPublic: () => api.get("/enums/mod-categories-public"),
  modTags: () => api.get("/enums/mod-tags"),
  laborTypes: () => api.get("/enums/labor-types"),
};

// Data Export/Import
export const dataAPI = {
  export: () => api.get("/export"),
  import: (data) => api.post("/import", data),
};

// Maintenance Templates
export const templatesAPI = {
  list: () => api.get("/templates"),
  get: (id) => api.get(`/templates/${id}`),
  apply: (vehicleId, templateId) => api.post(`/vehicles/${vehicleId}/apply-template/${templateId}`),
};

// Custom User Templates
export const userTemplatesAPI = {
  list: () => api.get("/user-templates"),
  get: (id) => api.get(`/user-templates/${id}`),
  create: (data) => api.post("/user-templates", data),
  update: (id, data) => api.put(`/user-templates/${id}`, data),
  delete: (id) => api.delete(`/user-templates/${id}`),
  apply: (vehicleId, templateId) => api.post(`/vehicles/${vehicleId}/apply-user-template/${templateId}`),
};

// Custom Categories (Pro)
export const customCategoriesAPI = {
  list: () => api.get("/custom-categories"),
  create: (data) => api.post("/custom-categories", data),
  rename: (id, data) => api.put(`/custom-categories/${id}`, data),
  delete: (id) => api.delete(`/custom-categories/${id}`),
};

// Analytics
export const analyticsAPI = {
  overview: () => api.get("/analytics/overview"),
  vehicle: (vehicleId) => api.get(`/analytics/vehicle/${vehicleId}`),
  forecast: () => api.get("/analytics/forecast"),
};

// Trail Logs
export const trailLogsAPI = {
  list: (vehicleId) => api.get(`/vehicles/${vehicleId}/trail-logs`),
  create: (vehicleId) => api.post(`/vehicles/${vehicleId}/trail-logs`),
  update: (logId, data) => api.put(`/trail-logs/${logId}`, data),
  delete: (logId) => api.delete(`/trail-logs/${logId}`),
  uploadGpx: (logId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post(`/trail-logs/${logId}/gpx`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  deleteGpx: (logId) => api.delete(`/trail-logs/${logId}/gpx`),
};

// Trips
export const tripsAPI = {
  list: () => api.get("/trips"),
  create: (data) => api.post("/trips", data),
  update: (id, data) => api.put(`/trips/${id}`, data),
  delete: (id) => api.delete(`/trips/${id}`),
};

// Community Trails
export const communityTrailsAPI = {
  list: (limit = 50, offset = 0) => api.get(`/community/trails?limit=${limit}&offset=${offset}`),
  get: (trailName) => api.get(`/community/trails/${encodeURIComponent(trailName)}`),
  submit: (data) => api.post("/community/trails", data),
};

// Social sharing OG
export const sharingOgAPI = {
  get: (token) => api.get(`/shared/${token}/og`),
};


// Wishlist
export const wishlistAPI = {
  list: (params = {}) => api.get("/wishlist", { params }),
  get: (id) => api.get(`/wishlist/${id}`),
  create: (item) => api.post("/wishlist", item),
  update: (id, item) => api.put(`/wishlist/${id}`, item),
  delete: (id) => api.delete(`/wishlist/${id}`),
  togglePurchased: (id) => api.post(`/wishlist/${id}/toggle-purchased`),
  priorities: () => api.get("/enums/wishlist-priorities"),
};

// Photo Gallery
export const galleryAPI = {
  list: (vehicleId, album = null) => {
    const params = album ? `?album=${album}` : "";
    return api.get(`/vehicles/${vehicleId}/gallery${params}`);
  },
  albums: (vehicleId) => api.get(`/vehicles/${vehicleId}/albums`),
  upload: (vehicleId, file, album = "Other", caption = "") => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("album", album);
    formData.append("caption", caption);
    return api.post(`/vehicles/${vehicleId}/gallery`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  update: (vehicleId, photoId, data) => 
    api.put(`/vehicles/${vehicleId}/gallery/${photoId}`, null, { params: data }),
  delete: (vehicleId, photoId) => api.delete(`/vehicles/${vehicleId}/gallery/${photoId}`),
  albumCategories: () => api.get("/enums/album-categories"),
};

// Reminders & Notifications
export const remindersAPI = {
  list: () => api.get("/reminders"),
  check: () => api.get("/reminders/check"),
  sendEmail: () => api.post("/reminders/send-email"),
};

export const notificationsAPI = {
  getPreferences: () => api.get("/notifications/preferences"),
  updatePreferences: (prefs) => api.put("/notifications/preferences", prefs),
};

// Push Notifications
export const pushAPI = {
  getVapidKey: () => api.get("/push/vapid-public-key"),
  subscribe: (subscription) => api.post("/push/subscribe", subscription),
  unsubscribe: () => api.delete("/push/unsubscribe"),
  status: () => api.get("/push/status"),
  sendTest: () => api.post("/push/send-test"),
  sendReminders: () => api.post("/push/send-reminders"),
};

// Public Sharing
export const sharingAPI = {
  toggle: (vehicleId) => api.post(`/vehicles/${vehicleId}/share`),
  status: (vehicleId) => api.get(`/vehicles/${vehicleId}/share-status`),
  getPublic: (shareToken) => api.get(`/shared/${shareToken}`),
};

// Parts Inventory
export const partsAPI = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/parts${query ? `?${query}` : ""}`);
  },
  get: (id) => api.get(`/parts/${id}`),
  create: (data) => api.post("/parts", data),
  update: (id, data) => api.put(`/parts/${id}`, data),
  delete: (id) => api.delete(`/parts/${id}`),
  adjustQuantity: (id, adjustment) => api.put(`/parts/${id}/adjust-quantity?adjustment=${adjustment}`),
  locations: () => api.get("/enums/part-locations"),
};

// VIN Decoder
export const vinAPI = {
  decode: (vin) => api.get(`/vin/decode/${vin}`),
};

// Teams
export const teamsAPI = {
  list: () => api.get("/teams"),
  get: (id) => api.get(`/teams/${id}`),
  create: (data) => api.post("/teams", data),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
  join: (inviteCode) => api.post(`/teams/join/${inviteCode}`),
  regenerateInvite: (id) => api.post(`/teams/${id}/regenerate-invite`),
  activity: (teamId, limit = 50) => api.get(`/teams/${teamId}/activity?limit=${limit}`),
  // Members
  addMember: (teamId, data) => api.post(`/teams/${teamId}/members`, data),
  updateMember: (teamId, userId, data) => api.put(`/teams/${teamId}/members/${userId}`, data),
  removeMember: (teamId, userId) => api.delete(`/teams/${teamId}/members/${userId}`),
  // Vehicles
  addVehicle: (teamId, vehicleId) => api.post(`/teams/${teamId}/vehicles`, { vehicle_id: vehicleId }),
  removeVehicle: (teamId, vehicleId) => api.delete(`/teams/${teamId}/vehicles/${vehicleId}`),
  roles: () => api.get("/enums/team-roles"),
};

// Activity Log
export const activityAPI = {
  team: (teamId, limit = 50) => api.get(`/teams/${teamId}/activity?limit=${limit}`),
  vehicle: (vehicleId, limit = 50) => api.get(`/vehicles/${vehicleId}/activity?limit=${limit}`),
};

// Service Shops
export const shopsAPI = {
  list: (teamId = null) => api.get(`/shops${teamId ? `?team_id=${teamId}` : ""}`),
  get: (id) => api.get(`/shops/${id}`),
  create: (data) => api.post("/shops", data),
  update: (id, data) => api.put(`/shops/${id}`, data),
  delete: (id) => api.delete(`/shops/${id}`),
  share: (shopId, teamId) => api.post(`/shops/${shopId}/share/${teamId}`),
  unshare: (shopId, teamId) => api.delete(`/shops/${shopId}/share/${teamId}`),
  search: (query, lat = null, lng = null) => {
    let url = `/shops/search/places?query=${encodeURIComponent(query)}`;
    if (lat && lng) url += `&latitude=${lat}&longitude=${lng}`;
    return api.get(url);
  },
  specialties: () => api.get("/enums/shop-specialties"),
};

// Subscription
export const subscriptionAPI = {
  status: () => api.get("/subscription/status"),
  features: () => api.get("/subscription/features"),
  usage: () => api.get("/subscription/usage"),
  checkout: (originUrl, paymentMethod = "stripe") => 
    api.post("/subscription/checkout", { origin_url: originUrl, payment_method: paymentMethod }),
  checkoutStatus: (sessionId) => api.get(`/subscription/checkout/status/${sessionId}`),
  applyUnlockCode: (code) => api.post("/subscription/unlock", { code }),
  // PayPal
  checkoutPayPal: (originUrl) => 
    api.post("/subscription/checkout/paypal", { origin_url: originUrl }),
  executePayPal: (paymentId, payerId) => 
    api.post(`/subscription/paypal/execute?payment_id=${paymentId}&payer_id=${payerId}`),
  paypalStatus: (paymentId) => api.get(`/subscription/paypal/status/${paymentId}`),
};

// AI Assistant
export const aiAPI = {
  chat: (message, sessionId) => {
    const token = localStorage.getItem("token");
    return fetch(`${API_BASE}/ai/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ message, session_id: sessionId }),
    });
  },
  sessions: () => api.get("/ai/sessions"),
  session: (id) => api.get(`/ai/sessions/${id}`),
  deleteSession: (id) => api.delete(`/ai/sessions/${id}`),
  gearChecklist: (tripId) => api.post("/ai/gear-checklist", { trip_id: tripId }),
};

// Weather
export const weatherAPI = {
  tripForecast: (tripId) => api.get(`/weather/trip/${tripId}`),
};

// Knowledge Base
export const knowledgeBaseAPI = {
  list: (category) => api.get(`/knowledge-base${category ? `?category=${encodeURIComponent(category)}` : ""}`),
  get: (id) => api.get(`/knowledge-base/${id}`),
  create: (data) => api.post("/knowledge-base", data),
  upvote: (id) => api.post(`/knowledge-base/${id}/upvote`),
  delete: (id) => api.delete(`/knowledge-base/${id}`),
};

// Admin Stats
export const adminAPI = {
  stats: () => api.get("/admin/stats"),
  logPageView: (page) => api.post("/analytics/page-view", { page }).catch(() => {}),
};

export default api;
